import { type ReactElement, useMemo, useState } from 'react';
import {
  Box,
  Divider,
  FormHelperText,
  InputBase,
  List,
  ListItemButton,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { MdCheck, MdKeyboardArrowDown } from 'react-icons/md';
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  getPhoneCountryByCode,
  PHONE_COUNTRIES,
  type PhoneCountry,
} from '../../constants/phone-country.constants';
import {
  convertToHalfWidth,
  sanitizePhoneNumberInput,
} from '../../utils/phone.utils';

interface PhoneNumberInputProps {
  countryCode: string;
  phoneNumber: string;
  onCountryCodeChange: (countryCode: string) => void;
  onPhoneNumberChange: (phoneNumber: string) => void;
  onPhoneNumberBlur?: () => void;
  error?: boolean;
  helperText?: string | undefined;
  placeholder?: string;
}

const getContainerStyles = (error: boolean) => ({
  display: 'flex',
  alignItems: 'stretch',
  minHeight: 40,
  backgroundColor: '#fbfdff',
  border: '1px solid',
  borderColor: error ? '#d32f2f' : '#e5ebf3',
  borderRadius: '4px',
  overflow: 'hidden',
  transition: 'box-shadow 120ms ease, border-color 120ms ease',
  '&:hover': {
    borderColor: error ? '#d32f2f' : '#cdd8e6',
  },
  '&:focus-within': {
    borderColor: error ? '#d32f2f' : '#0b5ed7',
    boxShadow: error ? 'none' : '0 0 0 3px rgba(11, 94, 215, 0.12)',
  },
});

const filterCountries = (
  countries: PhoneCountry[],
  keyword: string
): PhoneCountry[] => {
  const normalizedKeyword = keyword.trim().toLowerCase();
  if (!normalizedKeyword) {
    return countries;
  }

  return countries.filter(option => {
    return (
      option.name.toLowerCase().includes(normalizedKeyword) ||
      option.dialCode.includes(normalizedKeyword) ||
      option.code.toLowerCase().includes(normalizedKeyword) ||
      `+${option.dialCode}`.includes(normalizedKeyword)
    );
  });
};

const PhoneNumberInput = ({
  countryCode,
  phoneNumber,
  onCountryCodeChange,
  onPhoneNumberChange,
  onPhoneNumberBlur,
  error = false,
  helperText,
  placeholder = '電話番号を入力してください',
}: PhoneNumberInputProps): ReactElement => {
  const [countryAnchorEl, setCountryAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [searchValue, setSearchValue] = useState('');

  const selectedCountry = useMemo(
    () =>
      getPhoneCountryByCode(countryCode) ??
      getPhoneCountryByCode(DEFAULT_PHONE_COUNTRY_CODE)!,
    [countryCode]
  );

  const filteredCountries = useMemo(
    () => filterCountries(PHONE_COUNTRIES, searchValue),
    [searchValue]
  );

  const countryOpen = Boolean(countryAnchorEl);

  const handlePhoneNumberChange = (value: string): void => {
    onPhoneNumberChange(
      sanitizePhoneNumberInput(value, selectedCountry.maxLength)
    );
  };

  const handlePhoneNumberBlur = (): void => {
    onPhoneNumberChange(convertToHalfWidth(phoneNumber));
    onPhoneNumberBlur?.();
  };

  const handleCountrySelect = (country: PhoneCountry): void => {
    onCountryCodeChange(country.code);
    onPhoneNumberChange(
      sanitizePhoneNumberInput(phoneNumber, country.maxLength)
    );
    setCountryAnchorEl(null);
    setSearchValue('');
  };

  const handleCloseCountryPopover = (): void => {
    setCountryAnchorEl(null);
    setSearchValue('');
  };

  return (
    <Box>
      <Box sx={getContainerStyles(error)}>
        <Box
          role='button'
          tabIndex={0}
          onClick={event => setCountryAnchorEl(event.currentTarget)}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setCountryAnchorEl(event.currentTarget);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
            px: 1.25,
            minWidth: 72,
            flexShrink: 0,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <Typography component='span' sx={{ fontSize: 18, lineHeight: 1 }}>
            {selectedCountry.flag}
          </Typography>
          <MdKeyboardArrowDown size={18} color='#6b7280' />
        </Box>

        <Divider
          orientation='vertical'
          flexItem
          sx={{ borderColor: '#e5ebf3', alignSelf: 'stretch', my: '10px' }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            px: 1.75,
            py: '8.5px',
          }}
        >
          <Typography
            component='span'
            sx={{
              fontSize: 14,
              lineHeight: 1.4375,
              color: '#111827',
              whiteSpace: 'nowrap',
              mr: 0.75,
            }}
          >
            +{selectedCountry.dialCode}
          </Typography>
          <InputBase
            value={phoneNumber}
            onChange={event => handlePhoneNumberChange(event.target.value)}
            onBlur={handlePhoneNumberBlur}
            fullWidth
            autoComplete='off'
            placeholder={placeholder}
            sx={{
              flex: 1,
              fontSize: 14,
              lineHeight: 1.4375,
              color: '#111827',
              '& .MuiInputBase-input': {
                p: 0,
                height: 'auto',
                '&::placeholder': {
                  fontSize: 12,
                  opacity: 0.7,
                },
              },
            }}
          />
        </Box>
      </Box>

      <Popover
        open={countryOpen}
        anchorEl={countryAnchorEl}
        onClose={handleCloseCountryPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: 320,
              borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: '1px solid #e5ebf3' }}>
          <TextField
            autoFocus
            fullWidth
            size='small'
            placeholder='国を検索'
            value={searchValue}
            onChange={event => setSearchValue(event.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fbfdff',
                transition: 'box-shadow 120ms ease, border-color 120ms ease',
                '& fieldset': {
                  borderColor: '#e5ebf3',
                },
                '&:hover fieldset': {
                  borderColor: '#cdd8e6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0b5ed7',
                  boxShadow: '0 0 0 3px rgba(11, 94, 215, 0.12)',
                },
              },
            }}
          />
        </Box>

        <List
          dense
          sx={{
            maxHeight: 280,
            overflowY: 'auto',
            py: 0.5,
          }}
        >
          {filteredCountries.length > 0 ? (
            filteredCountries.map(option => {
              const isSelected = option.code === selectedCountry.code;

              return (
                <ListItemButton
                  key={option.code}
                  selected={isSelected}
                  onClick={() => handleCountrySelect(option)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    py: 1,
                    px: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography component='span' sx={{ fontSize: 18 }}>
                      {option.flag}
                    </Typography>
                    <Typography component='span' sx={{ fontSize: 14 }}>
                      {option.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      component='span'
                      sx={{ fontSize: 13, color: '#6b7280' }}
                    >
                      (+{option.dialCode})
                    </Typography>
                    {isSelected ? <MdCheck size={16} color='#0b5ed7' /> : null}
                  </Box>
                </ListItemButton>
              );
            })
          ) : (
            <Box sx={{ px: 1.5, py: 2 }}>
              <Typography sx={{ fontSize: 13, color: '#6b7280' }}>
                該当する国がありません
              </Typography>
            </Box>
          )}
        </List>
      </Popover>

      {helperText ? (
        <FormHelperText
          error={error}
          sx={{
            margin: '4px 0 0 0',
            fontSize: '12px',
            color: error ? undefined : '#6b7280',
          }}
        >
          {helperText}
        </FormHelperText>
      ) : null}
    </Box>
  );
};

export default PhoneNumberInput;
